import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CalendarClock,
  HandCoins,
  Plus,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { api } from '@/lib/api';
import { money, moneyRounded } from '@/lib/utils';
import { formatLongDate } from '@unifemga/shared';
import { Alert, Button, Card, EmptyState, PageHeader, Skeleton, Stat } from '@/components/ui';
import { RepayModal } from '@/components/RepayModal';
import { StatusBadge } from '@/components/StatusBadge';
import { KYC_STATUS, LOAN_STATUS } from '@/lib/status';

type DashboardLoan = DashboardResponse['loans'][number];

interface DashboardResponse {
  memberName: string;
  membershipNumber: string;
  kycStatus: string;
  activeLoans: number;
  outstandingBalance: string;
  totalRepaid: string;
  overdueAmount: string;
  nextRepayment: { dueDate: string; amount: string } | null;
  loans: Array<{
    id: string;
    loanNumber: string;
    productName: string;
    status: string;
    principal: string;
    totalRepayable: string;
    amountRepaid: string;
    outstanding: string;
    progress: number;
    daysInArrears: number;
    nextPayment: { dueDate: string; amount: string } | null;
  }>;
}

export default function PortalDashboard() {
  const [repaying, setRepaying] = useState<DashboardLoan | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['member-dashboard'],
    queryFn: () => api.get<DashboardResponse>('/loans/my/dashboard'),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const kycApproved = data.kycStatus === 'APPROVED';
  const hasArrears = Number(data.overdueAmount) > 0;

  return (
    <div>
      <PageHeader
        eyebrow={`Membership ${data.membershipNumber}`}
        title={`Assalamu alaikum, ${data.memberName.split(' ')[0]}`}
        description="Your loans and repayments at a glance."
        actions={
          kycApproved ? (
            <Link to="/portal/loans/apply">
              <Button>
                <Plus size={16} /> Apply for a loan
              </Button>
            </Link>
          ) : undefined
        }
      />

      {!kycApproved && (
        <div className="mb-6 rounded-2xl border border-warning/30 bg-warning-soft p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 gap-3.5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warning/15 text-warning">
                <ShieldCheck size={19} />
              </span>
              <div className="min-w-0">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h2 className="font-heading font-bold text-warning-soft-foreground">
                    Complete your verification
                  </h2>
                  <StatusBadge map={KYC_STATUS} status={data.kycStatus} />
                </div>
                <p className="text-sm leading-relaxed text-warning-soft-foreground/85">
                  Your identity has to be verified before you can apply for a loan.
                </p>
              </div>
            </div>

            <Link to="/portal/kyc" className="shrink-0">
              <Button full className="sm:w-auto">
                Continue <ArrowRight size={15} />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {hasArrears && (
        <Alert tone="danger" title="You have overdue repayments" className="mb-6">
          {money(data.overdueAmount)} is past due. Please bring your account up to date — UNIFEMGA
          charges no late fees, but overdue repayments suspend your eligibility to borrow again.
        </Alert>
      )}

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Outstanding balance"
          value={moneyRounded(data.outstandingBalance)}
          hint={`${data.activeLoans} active loan${data.activeLoans === 1 ? '' : 's'}`}
          icon={<Wallet size={16} />}
        />
        <Stat
          label="Next repayment"
          value={data.nextRepayment ? moneyRounded(data.nextRepayment.amount) : '—'}
          hint={
            data.nextRepayment
              ? `Due ${formatLongDate(new Date(data.nextRepayment.dueDate))}`
              : 'Nothing scheduled'
          }
          icon={<CalendarClock size={16} />}
        />
        <Stat
          label="Total repaid"
          value={moneyRounded(data.totalRepaid)}
          tone="success"
          icon={<HandCoins size={16} />}
        />
        <Stat
          label="Overdue"
          value={moneyRounded(data.overdueAmount)}
          tone={hasArrears ? 'danger' : 'success'}
          hint={hasArrears ? 'Action needed' : 'Up to date'}
          icon={<AlertTriangle size={16} />}
        />
      </div>

      <h2 className="mb-4 font-heading text-lg font-bold text-foreground">Your loans</h2>

      {data.loans.length === 0 ? (
        <EmptyState
          icon={<HandCoins size={20} />}
          title="No loans yet"
          description="When you take an interest-free AAF-UNIFEMGA loan, it will appear here with its full repayment schedule."
          action={
            kycApproved ? (
              <Link to="/portal/loans/apply">
                <Button>Apply for a loan</Button>
              </Link>
            ) : (
              <Link to="/portal/kyc">
                <Button variant="outline">Complete verification first</Button>
              </Link>
            )
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.loans.map((loan) => (
            <Link key={loan.id} to={`/portal/loans/${loan.id}`}>
              <Card className="p-5 transition-shadow hover:shadow-md">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="numeric font-heading font-bold text-foreground">
                      {loan.loanNumber}
                    </p>
                    <p className="text-sm text-foreground-secondary">{loan.productName}</p>
                  </div>
                  <StatusBadge map={LOAN_STATUS} status={loan.status} />
                </div>

                {/* Borrowed vs repayable, side by side. On a Qard Hasan loan
                    these are the same number, and showing both makes that
                    visible rather than something the member has to trust. */}
                <div className="mb-4 flex items-end justify-between gap-4 rounded-lg bg-muted px-4 py-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-secondary">
                      Loan amount
                    </p>
                    <p className="numeric font-semibold text-foreground-secondary">
                      {money(loan.principal)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-secondary">
                      Outstanding
                    </p>
                    <p className="numeric text-lg font-bold leading-none text-foreground">
                      {money(loan.outstanding)}
                    </p>
                  </div>
                </div>

                <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-success transition-all"
                    style={{ width: `${Math.round(loan.progress * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-foreground-secondary">
                  <span className="numeric">{money(loan.amountRepaid)} repaid</span>
                  <span className="numeric font-semibold text-foreground">
                    {money(loan.outstanding)} left
                  </span>
                </div>

                {loan.nextPayment && (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
                    <p className="text-xs text-foreground-secondary">
                      Next:{' '}
                      <span className="numeric font-semibold text-foreground">
                        {money(loan.nextPayment.amount)}
                      </span>{' '}
                      on {formatLongDate(new Date(loan.nextPayment.dueDate))}
                    </p>
                    {/* Inside a card that is itself a link, so the click has to
                        be stopped from navigating before the modal opens. */}
                    <Button
                      size="sm"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setRepaying(loan);
                      }}
                    >
                      <Banknote size={14} /> Repay
                    </Button>
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}

      {repaying && (
        <RepayModal
          loanId={repaying.id}
          loanNumber={repaying.loanNumber}
          nextInstallment={
            repaying.nextPayment
              ? {
                  installmentNumber: 0,
                  dueDate: repaying.nextPayment.dueDate,
                  amountDue: repaying.nextPayment.amount,
                }
              : null
          }
          onClose={() => setRepaying(null)}
        />
      )}
    </div>
  );
}
